import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getCart, addToCart, removeFromCart, updateCartItem } from '../api/cart'

const CartContext = createContext(null)

export function CartProvider({ children }) {
  const [cart,      setCart]      = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isOpen,    setIsOpen]    = useState(false)
  const [pendingId, setPendingId] = useState(null)

  const refreshCart = useCallback(async () => {
    try {
      const data = await getCart()
      setCart(data)
    } catch {
      // ignore — guest cart may be empty or CORS not yet configured
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { refreshCart() }, [refreshCart])

  const addItem = useCallback(async (productId, quantity = 1) => {
    setPendingId(productId)
    try {
      const data = await addToCart(productId, quantity)
      setCart(data)
      setIsOpen(true)
    } finally {
      setPendingId(null)
    }
  }, [])

  const removeItem = useCallback(async (key) => {
    const data = await removeFromCart(key)
    setCart(data)
  }, [])

  const updateItem = useCallback(async (key, quantity) => {
    if (quantity < 1) return removeItem(key)
    const data = await updateCartItem(key, quantity)
    setCart(data)
  }, [removeItem])

  const minorUnit   = cart?.totals?.currency_minor_unit ?? 2
  const formatPrice = (raw) =>
    (parseInt(raw || 0) / Math.pow(10, minorUnit)).toLocaleString('en-EG')

  return (
    <CartContext.Provider value={{
      cart, isLoading, isOpen, setIsOpen, pendingId,
      addItem, removeItem, updateItem,
      itemCount:      cart?.items_count ?? 0,
      formatPrice,
      currencySymbol: cart?.totals?.currency_symbol ?? 'EGP',
      refreshCart,
    }}>
      {children}
    </CartContext.Provider>
  )
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used within <CartProvider>')
  return ctx
}
